import React, { useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X, ChevronLeft, ChevronRight, Minus, Plus, Check, Loader2, ShieldCheck } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { useBooking, API, VILLA } from "../data";

const toISO = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
const DOW = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];

function MonthGrid({ year, month, blocked, range, onPick }) {
    const today = toISO(new Date());
    const first = new Date(year, month, 1);
    const days = new Date(year, month + 1, 0).getDate();
    const offset = first.getDay();
    const cells = [];
    for (let i = 0; i < offset; i++) cells.push(null);
    for (let d = 1; d <= days; d++) cells.push(toISO(new Date(year, month, d)));

    return (
        <div>
            <p className="text-center font-serif-d text-lg text-[#0F2837] mb-3">{MONTHS[month]} {year}</p>
            <div className="grid grid-cols-7 gap-1 text-center text-[10px] uppercase tracking-widest text-[#5C6A78] mb-1">
                {DOW.map((d) => <span key={d}>{d}</span>)}
            </div>
            <div className="grid grid-cols-7 gap-1">
                {cells.map((iso, i) => {
                    if (!iso) return <span key={`e${i}`} />;
                    const isPast = iso < today;
                    const isBlocked = blocked.has(iso);
                    const isEdge = iso === range.checkIn || iso === range.checkOut;
                    const inRange = range.checkIn && range.checkOut && iso > range.checkIn && iso < range.checkOut;
                    const cls = isBlocked ? "cal-disabled" : isPast ? "cal-past" : "";
                    return (
                        <button key={iso} type="button" disabled={isPast || isBlocked} onClick={() => onPick(iso)}
                            data-testid={`cal-day-${iso}`}
                            className={`cal-day ${cls} ${inRange ? "cal-in-range" : ""} ${isEdge ? "cal-edge" : ""}`}>
                            {Number(iso.slice(-2))}
                        </button>
                    );
                })}
            </div>
        </div>
    );
}

export default function BookingModal() {
    const { availability, modalOpen, setModalOpen, range, setRange, guests, setGuests, fetchAvailability } = useBooking();
    const [monthOffset, setMonthOffset] = useState(0);
    const [step, setStep] = useState(1);
    const [form, setForm] = useState({ name: "", email: "", phone: "", notes: "" });
    const [submitting, setSubmitting] = useState(false);
    const [confirmation, setConfirmation] = useState(null);

    const blocked = useMemo(() => new Set(availability?.blocked_dates || []), [availability]);
    const base = new Date();
    const m1 = new Date(base.getFullYear(), base.getMonth() + monthOffset, 1);
    const m2 = new Date(base.getFullYear(), base.getMonth() + monthOffset + 1, 1);

    const nights = useMemo(() => {
        if (!range.checkIn || !range.checkOut) return 0;
        return Math.round((new Date(range.checkOut) - new Date(range.checkIn)) / 86400000);
    }, [range]);

    const quote = useMemo(() => {
        if (!availability || nights < 1) return null;
        const subtotal = nights * availability.nightly_rate;
        const taxes = Math.round(subtotal * availability.tax_fees_rate * 100) / 100;
        const total = Math.round((subtotal + taxes + availability.cleaning_fee) * 100) / 100;
        return { subtotal, taxes, total, cleaning: availability.cleaning_fee, rate: availability.nightly_rate };
    }, [availability, nights]);

    const pick = (iso) => {
        setStep(1);
        if (!range.checkIn || (range.checkIn && range.checkOut)) {
            setRange({ checkIn: iso, checkOut: null });
            return;
        }
        if (iso <= range.checkIn) { setRange({ checkIn: iso, checkOut: null }); return; }
        let cur = new Date(range.checkIn);
        const end = new Date(iso);
        while (cur < end) {
            if (blocked.has(toISO(cur))) { toast.error("Your range includes unavailable dates."); setRange({ checkIn: iso, checkOut: null }); return; }
            cur = new Date(cur.getTime() + 86400000);
        }
        setRange({ ...range, checkOut: iso });
    };

    const close = () => { setModalOpen(false); setStep(1); setConfirmation(null); };

    const submit = async () => {
        setSubmitting(true);
        try {
            const { data } = await axios.post(`${API}/bookings`, {
                check_in: range.checkIn, check_out: range.checkOut, guests,
                name: form.name, email: form.email, phone: form.phone, notes: form.notes,
            });
            setConfirmation(data);
            setStep(3);
            fetchAvailability();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Could not reserve these dates. Please adjust your selection.");
            fetchAvailability();
            setStep(1);
        } finally {
            setSubmitting(false);
        }
    };

    const fmt = (iso) => iso ? new Date(iso + "T12:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "—";

    return (
        <AnimatePresence>
            {modalOpen && (
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                    className="fixed inset-0 z-[90] bg-[#0F2837]/70 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-6"
                    onClick={close} data-testid="booking-modal-overlay">
                    <motion.div initial={{ y: 60, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 60, opacity: 0 }} transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
                        onClick={(e) => e.stopPropagation()}
                        className="bg-[#FAF8F5] w-full max-w-4xl max-h-[92vh] overflow-y-auto rounded-t-3xl sm:rounded-3xl shadow-2xl"
                        data-testid="booking-modal">
                        <div className="sticky top-0 bg-[#FAF8F5]/95 backdrop-blur px-6 py-4 flex items-center justify-between border-b border-[#E5DCCB] z-10">
                            <div>
                                <p className="text-[10px] uppercase tracking-[0.25em] text-[#C49A45] font-semibold">Reserve your stay</p>
                                <p className="font-serif-d text-xl text-[#0F2837]">{VILLA.name} {VILLA.unit}</p>
                            </div>
                            <button onClick={close} className="p-2 rounded-full hover:bg-[#F3EDE2]" data-testid="booking-modal-close" aria-label="Close"><X size={20} /></button>
                        </div>

                        {step === 3 && confirmation ? (
                            <div className="px-6 py-14 text-center" data-testid="booking-confirmation">
                                <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: "spring", stiffness: 200, delay: 0.1 }}
                                    className="mx-auto w-16 h-16 rounded-full bg-[#2D6A76] flex items-center justify-center text-white mb-6">
                                    <Check size={30} />
                                </motion.div>
                                <h3 className="font-serif-d text-3xl text-[#0F2837]">Your dates are reserved.</h3>
                                <p className="mt-3 text-[#5C6A78] max-w-md mx-auto text-sm leading-relaxed">
                                    {confirmation.message} A confirmation was logged for <strong>{fmt(confirmation.check_in)} → {fmt(confirmation.check_out)}</strong>, {confirmation.nights} nights, {confirmation.guests} guests.
                                </p>
                                <div className="mt-6 inline-flex flex-col rounded-2xl border border-[#E5DCCB] bg-white px-8 py-5">
                                    <span className="text-[10px] uppercase tracking-[0.25em] text-[#5C6A78]">Estimated total</span>
                                    <span className="font-serif-d text-4xl text-[#0F2837]" data-testid="booking-confirmation-total">${confirmation.total.toLocaleString()}</span>
                                    <span className="text-xs text-[#5C6A78] mt-1">Reference {confirmation.id.slice(0, 8).toUpperCase()}</span>
                                </div>
                                <div className="mt-8">
                                    <button onClick={close} className="btn-primary-lux rounded-full px-8 py-3 text-sm font-semibold" data-testid="booking-confirmation-done">Done</button>
                                </div>
                            </div>
                        ) : (
                            <div className="grid lg:grid-cols-[1.4fr_1fr]">
                                <div className="p-6 border-b lg:border-b-0 lg:border-r border-[#E5DCCB]">
                                    <div className="flex items-center justify-between mb-4">
                                        <button onClick={() => setMonthOffset(Math.max(0, monthOffset - 1))} disabled={monthOffset === 0} className="p-2 rounded-full hover:bg-[#F3EDE2] disabled:opacity-30" data-testid="cal-prev-month" aria-label="Previous month"><ChevronLeft size={18} /></button>
                                        <p className="text-xs text-[#5C6A78]">Select check-in, then check-out · min {availability?.min_nights || 2} nights</p>
                                        <button onClick={() => setMonthOffset(monthOffset + 1)} className="p-2 rounded-full hover:bg-[#F3EDE2]" data-testid="cal-next-month" aria-label="Next month"><ChevronRight size={18} /></button>
                                    </div>
                                    <div className="grid sm:grid-cols-2 gap-6" data-testid="availability-calendar">
                                        <MonthGrid year={m1.getFullYear()} month={m1.getMonth()} blocked={blocked} range={range} onPick={pick} />
                                        <MonthGrid year={m2.getFullYear()} month={m2.getMonth()} blocked={blocked} range={range} onPick={pick} />
                                    </div>
                                    <div className="mt-4 flex items-center gap-4 text-xs text-[#5C6A78]">
                                        <span className="flex items-center gap-2"><span className="w-3 h-3 rounded-full bg-[#0F2837] inline-block" /> Selected</span>
                                        <span className="flex items-center gap-2"><span className="w-3 h-3 rounded-full bg-[#E5DCCB] inline-block line-through" /> Reserved</span>
                                    </div>
                                </div>

                                <div className="p-6 flex flex-col">
                                    <div className="rounded-2xl border border-[#E5DCCB] bg-white p-5">
                                        <div className="grid grid-cols-2 gap-4 text-sm">
                                            <div><p className="text-[10px] uppercase tracking-[0.2em] text-[#5C6A78]">Check-in</p><p className="font-semibold text-[#0F2837]" data-testid="summary-checkin">{fmt(range.checkIn)}</p></div>
                                            <div><p className="text-[10px] uppercase tracking-[0.2em] text-[#5C6A78]">Check-out</p><p className="font-semibold text-[#0F2837]" data-testid="summary-checkout">{fmt(range.checkOut)}</p></div>
                                        </div>
                                        <div className="mt-4 flex items-center justify-between">
                                            <p className="text-[10px] uppercase tracking-[0.2em] text-[#5C6A78]">Guests</p>
                                            <div className="flex items-center gap-3" data-testid="guests-stepper">
                                                <button onClick={() => setGuests(Math.max(1, guests - 1))} className="w-8 h-8 rounded-full border border-[#E5DCCB] flex items-center justify-center hover:bg-[#F3EDE2]" data-testid="guests-decrease" aria-label="Fewer guests"><Minus size={14} /></button>
                                                <span className="font-semibold text-[#0F2837] w-4 text-center" data-testid="guests-count">{guests}</span>
                                                <button onClick={() => setGuests(Math.min(availability?.max_guests || 6, guests + 1))} className="w-8 h-8 rounded-full border border-[#E5DCCB] flex items-center justify-center hover:bg-[#F3EDE2]" data-testid="guests-increase" aria-label="More guests"><Plus size={14} /></button>
                                            </div>
                                        </div>
                                    </div>

                                    {quote && nights >= (availability?.min_nights || 2) ? (
                                        <div className="mt-4 rounded-2xl border border-[#E5DCCB] bg-white p-5 text-sm" data-testid="price-breakdown">
                                            <div className="flex justify-between py-1"><span className="text-[#5C6A78]">${quote.rate} × {nights} nights</span><span className="font-medium">${quote.subtotal.toLocaleString()}</span></div>
                                            <div className="flex justify-between py-1"><span className="text-[#5C6A78]">Cleaning fee</span><span className="font-medium">${quote.cleaning}</span></div>
                                            <div className="flex justify-between py-1"><span className="text-[#5C6A78]">Taxes & fees</span><span className="font-medium">${quote.taxes.toLocaleString()}</span></div>
                                            <div className="flex justify-between pt-3 mt-2 border-t border-[#E5DCCB]"><span className="font-semibold text-[#0F2837]">Total</span><span className="font-serif-d text-2xl text-[#0F2837]" data-testid="price-total">${quote.total.toLocaleString()}</span></div>
                                        </div>
                                    ) : (
                                        <p className="mt-4 text-xs text-[#5C6A78] rounded-2xl border border-dashed border-[#E5DCCB] p-4" data-testid="price-hint">
                                            {range.checkIn && range.checkOut ? `Minimum stay is ${availability?.min_nights || 2} nights.` : "Pick your dates on the calendar to see your quote."}
                                        </p>
                                    )}

                                    {quote && nights >= (availability?.min_nights || 2) && (
                                        <div className="mt-4 space-y-3" data-testid="guest-details-form">
                                            <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Full name" data-testid="booking-name-input"
                                                className="w-full rounded-xl border border-[#E5DCCB] bg-white px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-[#C49A45]/50" />
                                            <input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="Email address" type="email" data-testid="booking-email-input"
                                                className="w-full rounded-xl border border-[#E5DCCB] bg-white px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-[#C49A45]/50" />
                                            <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="Phone (optional)" data-testid="booking-phone-input"
                                                className="w-full rounded-xl border border-[#E5DCCB] bg-white px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-[#C49A45]/50" />
                                        </div>
                                    )}

                                    <div className="mt-auto pt-5">
                                        <button
                                            disabled={!quote || nights < (availability?.min_nights || 2) || !form.name || !form.email || submitting}
                                            onClick={submit} data-testid="reserve-submit-button"
                                            className="btn-amber-lux w-full rounded-2xl py-4 text-sm font-bold tracking-wide disabled:opacity-40 disabled:cursor-not-allowed disabled:transform-none flex items-center justify-center gap-2">
                                            {submitting ? <><Loader2 size={16} className="animate-spin" /> Reserving…</> : `Reserve ${nights > 0 ? `· ${nights} nights` : ""}`}
                                        </button>
                                        <p className="mt-3 flex items-center justify-center gap-2 text-[11px] text-[#5C6A78]">
                                            <ShieldCheck size={13} className="text-[#2D6A76]" /> You will not be charged yet · Free date changes on request
                                        </p>
                                    </div>
                                </div>
                            </div>
                        )}
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );
}
